import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, TextField, Typography, useTheme } from "@material-ui/core";
import React, { useContext, useEffect, useRef, useState } from "react";
import ExpoContext from "../../../contexts/ExpoContext";
import { Entry } from "../../../model/Entry";
import { initNewOrder, Order } from "../../../model/Order";
import { OrderContact } from "../../../model/OrderContact";
import { OrderItem } from "../../../model/OrderItem";
import { PointOfSale } from "../../../model/PointOfSale";
import { Product } from "../../../model/Product";
import { ApiBackend } from "../../../providers/apibackend";
import withAlert, { IAlertContext } from "../../Common/dialog/Alert";
import SelectPointOfSale from "../../Orders/SelectPointOfSale";
import StripePaymentStep from "../../Orders/Payments/StripePamentStep";
import SwishPaymentStep from "../../Orders/Payments/SwishPaymentStep";

interface IProps {
    entry: Entry;
    /** Loppprodukten anmälan gäller - dess addOnProducts avgör vilka tillägg som kan väljas */
    raceProduct: Product;
    onSave: () => void;
    onAbort: () => void;
}

type PaymentType = "link" | "account" | "card" | "swish";

const awaiter = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Bygger metadatan som kopplar en addon-orderrad till en anmälan.
 * Speglar backend MoveEntryAddonToOrderItemRunner.createCommonOrderItem så att tillägget
 * hittas av anmälningskortets entryId-uppslag och får korrekt utlämnings-/leveransinfo.
 */
const buildAddonMetadata = (entry: Entry): { key: string, value: string }[] => {
    return [
        { key: "entryId", value: entry.id },
        { key: "personId", value: entry.personId },
        { key: "firstName", value: entry.firstName },
        { key: "lastName", value: entry.lastName },
        { key: "address", value: entry.address },
        { key: "postalCode", value: entry.postalCode },
        { key: "city", value: entry.city },
        { key: "country", value: entry.country },
        { key: "nationality", value: entry.nationality },
        { key: "email", value: entry.email },
        { key: "phone", value: entry.phone }
    ];
};

const AddEntryAddonDialog = ({ entry, raceProduct, onSave, onAbort, showAlert }: IProps & IAlertContext) => {
    const [step, setStep] = useState<"select" | "order">("select");
    const [addonProducts, setAddonProducts] = useState<Product[]>(null);
    const [productId, setProductId] = useState("");
    const [variantId, setVariantId] = useState("");
    const [priceGroupId, setPriceGroupId] = useState("");

    const [kortterminalPos, setKortterminalPos] = useState<PointOfSale>(null);
    const [pointOfSale, setPointOfSale] = useState("");
    const [paymentComment, setPaymentComment] = useState("");
    const [paymentType, setPaymentType] = useState<PaymentType>("link");
    const [contactEmail, setContactEmail] = useState(entry.email ?? "");
    const [phone, setPhone] = useState(entry.phone ?? "");
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);
    const [createOrderStatus, setCreateOrderStatus] = useState("");

    const didMount = useRef<boolean>(false);
    const backend = new ApiBackend();
    const theme = useTheme();
    const expoContext = useContext(ExpoContext);
    // Kortbetalning kräver en konfigurerad kortterminal (POS-läge) - annars kraschar StripePaymentStep
    const cardPaymentAvailable = !!expoContext.userStripeTerminal;

    useEffect(() => {
        didMount.current = true;

        const init = async (): Promise<void> => {
            const [addons, pos] = await Promise.all([
                loadAddonProducts(),
                backend.getKortterminalVlPointOfSale()
            ]);

            if (didMount.current) {
                setAddonProducts(addons);
                setKortterminalPos(pos);
            }
        };

        init();

        return () => { didMount.current = false; };
    }, []);

    const loadAddonProducts = async (): Promise<Product[]> => {
        const ids = raceProduct.addOnProducts;
        if (!ids || ids.length === 0) {
            return [];
        }

        const products = await Promise.all(ids.map(id => backend.getProduct(id)));
        // Försäkring är knuten till person och hanteras inte här (samma exkludering som vid flytt av tillägg)
        return products.filter(p => p && !p.IsArchived && p.type !== "insurance");
    };

    const selectedProduct = addonProducts?.find(p => p.id === productId);
    const selectedVariant = selectedProduct?.variants?.find(v => v.Id === variantId);
    const selectedPriceGroup = selectedVariant?.priceGroups?.find(pg => pg.id === priceGroupId);
    const price = selectedPriceGroup?.price ?? 0;
    const hasValidSelection = !!selectedProduct && !!selectedVariant && !!selectedPriceGroup;

    const handleProductChange = (value: string): void => {
        setProductId(value);
        setVariantId("");
        setPriceGroupId("");
    };

    const handleVariantChange = (value: string): void => {
        setVariantId(value);
        setPriceGroupId("");
    };

    const handlePaymentTypeChecked = (type: PaymentType): void => {
        if (type === "card") {
            setPointOfSale(kortterminalPos?.id ?? "");
        } else {
            setPointOfSale("");
        }
        setPaymentType(type);
    };

    const buildOrder = (): Order => {
        const order = initNewOrder();
        order.locale = entry.nationality === "SWE" || entry.nationality === "NOR" ? "se" : "en";
        order.contact = {
            firstName: entry.firstName,
            lastName: entry.lastName,
            address: entry.address,
            postalcode: entry.postalCode,
            city: entry.city,
            country: entry.country,
            email: paymentType === "link" ? contactEmail : entry.email,
            phone: phone,
            loggedInUserId: entry.personId
        } as OrderContact;

        const oi = {
            numberOfProducts: 1,
            productId: selectedProduct.id,
            productVariantId: selectedVariant.Id,
            productVariantPriceGroupId: selectedPriceGroup.id,
            itemPrice: selectedPriceGroup.price,
            productName: selectedProduct.name,
            variantName: selectedVariant.Name,
            priceGroupName: selectedPriceGroup.Name,
            metadata: buildAddonMetadata(entry)
        } as OrderItem;

        order.items = [oi];
        // Vid betallänk: lås ordern så vald prisgrupp gäller och inget kan ändras på checkout-sidan
        if (paymentType === "link") {
            order.locked = true;
        }

        return order;
    };

    const getCreateOrder = async (): Promise<Order> => {
        setIsCreatingOrder(true);
        setCreateOrderStatus("Skapar order");

        const res = await backend.adminCreateOrder(buildOrder());
        if (!res) {
            throw ("Kunde inte skapa order för tillägget");
        }

        // Gratis tillägg blir direkt slutförd order - ingen betalning behövs
        if (res.totalSum <= 0) {
            onSave();
            return null;
        }

        return res;
    };

    const createAddon = async (): Promise<void> => {
        try {
            const createdOrder = await getCreateOrder();
            if (!createdOrder) {
                return;
            }

            let work: Promise<boolean>;
            if (paymentType === "account") {
                setCreateOrderStatus("Registrerar betalning");
                work = backend.addOrderPayment({
                    orderId: createdOrder.id,
                    paymentType: "PAYMENT",
                    amount: createdOrder.totalSum,
                    pointOfSaleId: pointOfSale,
                    description: paymentComment
                });
            } else if (paymentType === "link") {
                setCreateOrderStatus("Skickar länk till betalning");
                work = backend.sendOrderPaymentRequest(createdOrder.id);
            }

            if (work) {
                await work;
            }

            setCreateOrderStatus("Klart!");
            onSave();
        } catch (err) {
            showAlert("Fel", typeof err === "string" ? err : "Ett fel uppstod när tillägget skulle läggas till.");
            setIsCreatingOrder(false);
        }
    };

    const renderSelectStep = () => {
        const gridStyle = { background: theme.palette.primary.dark, color: theme.palette.primary.main };

        if (!addonProducts) {
            return <Box display="flex" justifyContent="center" p={4}><CircularProgress color="secondary" /></Box>;
        }

        if (addonProducts.length === 0) {
            return <Typography>Det finns inga tillgängliga tillägg för detta lopp.</Typography>;
        }

        return (
            <Grid container spacing={2}>
                <Grid item xs={12} style={gridStyle}>Välj tillägg</Grid>
                <Grid item xs={5}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Produkt</InputLabel>
                        <Select
                            value={productId}
                            onChange={(ev) => handleProductChange(ev.target.value as string)}
                        >
                            {addonProducts.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Variant</InputLabel>
                        <Select
                            disabled={!selectedProduct}
                            value={variantId}
                            onChange={(ev) => handleVariantChange(ev.target.value as string)}
                        >
                            {selectedProduct?.variants?.map(v => <MenuItem key={v.Id} value={v.Id}>{v.Name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Prisgrupp</InputLabel>
                        <Select
                            disabled={!selectedVariant}
                            value={priceGroupId}
                            onChange={(ev) => setPriceGroupId(ev.target.value as string)}
                        >
                            {selectedVariant?.priceGroups?.map(pg => <MenuItem key={pg.id} value={pg.id}>{pg.Name} ({pg.price} kr)</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} style={gridStyle}>Läggs till på anmälan</Grid>
                <Grid item xs={12}>
                    <Typography variant="body2">
                        {entry.firstName} {entry.lastName}{entry.email ? ` · ${entry.email}` : ""}
                    </Typography>
                </Grid>
            </Grid>
        );
    };

    const renderOrderStep = () => {
        return (
            <Grid container spacing={2}>
                {price === 0 &&
                    <Grid item xs={12} style={{ background: theme.palette.primary.dark, color: theme.palette.primary.main }}>
                        Ingen betalning behövs
                    </Grid>
                }
                {price > 0 && <>
                    <Grid item xs={12} style={{ background: theme.palette.primary.dark, color: theme.palette.primary.main }}>
                        Betalning
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel label="Skicka betallänk"
                            control={<Checkbox checked={paymentType === "link"}
                                onChange={(ev) => { if (ev.target.checked) handlePaymentTypeChecked("link"); }} />} />
                        <FormControlLabel label="Boka mot konto"
                            control={<Checkbox checked={paymentType === "account"}
                                onChange={(ev) => { if (ev.target.checked) handlePaymentTypeChecked("account"); }} />} />
                        <FormControlLabel label="Kortbetalning"
                            disabled={!cardPaymentAvailable}
                            control={<Checkbox checked={paymentType === "card"}
                                disabled={!cardPaymentAvailable}
                                onChange={(ev) => { if (ev.target.checked) handlePaymentTypeChecked("card"); }} />} />
                        {!cardPaymentAvailable &&
                            <Typography variant="caption" color="error" style={{ marginRight: 16 }}>
                                Ingen kortterminal konfigurerad
                            </Typography>
                        }
                        <FormControlLabel label="Swish"
                            control={<Checkbox checked={paymentType === "swish"}
                                onChange={(ev) => { if (ev.target.checked) handlePaymentTypeChecked("swish"); }} />} />
                    </Grid>

                    {paymentType === "link" &&
                        <Grid item xs={12}>
                            <TextField label="E-post" fullWidth InputLabelProps={{ shrink: true }}
                                value={contactEmail}
                                onChange={(ev) => setContactEmail(ev.target.value)} />
                        </Grid>
                    }

                    {paymentType === "account" && <>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel shrink>POS</InputLabel>
                                <SelectPointOfSale onChange={setPointOfSale} />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField label="Kommentar" fullWidth InputLabelProps={{ shrink: true }}
                                value={paymentComment}
                                onChange={(ev) => setPaymentComment(ev.target.value)} />
                        </Grid>
                    </>}

                    {paymentType === "card" &&
                        <StripePaymentStep
                            initializePaymentDisabled={isCreatingOrder}
                            onRequestCreateOrder={getCreateOrder}
                            onProgress={setCreateOrderStatus}
                            onComplete={async () => {
                                setIsCreatingOrder(false);
                                await awaiter(3000);
                                onSave();
                            }}
                            onAbort={() => setIsCreatingOrder(false)} />
                    }

                    {paymentType === "swish" &&
                        <SwishPaymentStep
                            phone={phone}
                            initializePaymentDisabled={isCreatingOrder}
                            onPhoneChange={setPhone}
                            onRequestCreateOrder={getCreateOrder}
                            onProgress={setCreateOrderStatus}
                            onComplete={async () => {
                                setIsCreatingOrder(false);
                                await awaiter(3000);
                                onSave();
                            }}
                            onAbort={() => setIsCreatingOrder(false)} />
                    }

                    {isCreatingOrder &&
                        <Grid item xs={12} style={{ background: theme.palette.primary.dark, color: theme.palette.primary.main, display: "flex", alignItems: "center", gap: 12 }}>
                            <CircularProgress size={20} />
                            {createOrderStatus}
                        </Grid>
                    }
                </>}
            </Grid>
        );
    };

    const showCreateBtn = () => price === 0 || (paymentType !== "card" && paymentType !== "swish");

    return (
        <Dialog disableEscapeKeyDown fullWidth maxWidth="md" open={true}>
            <DialogTitle>Lägg till tillägg</DialogTitle>
            <DialogContent dividers style={{ minHeight: 200 }}>
                {step === "select" && renderSelectStep()}
                {step === "order" && renderOrderStep()}
            </DialogContent>
            <DialogActions>
                <Box style={{ display: "flex", width: "100%", alignItems: "center" }}>
                    <Box style={{ flex: 1 }}>
                        {hasValidSelection && <>Totalt pris: <b>{price} kr</b></>}
                    </Box>
                    <Box style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        {step === "order" &&
                            <Button variant="contained" color="secondary"
                                disabled={isCreatingOrder}
                                onClick={() => setStep("select")}>Föregående</Button>
                        }
                        {step === "select" &&
                            <Button variant="contained" color="secondary"
                                disabled={!hasValidSelection}
                                onClick={() => setStep("order")}>Nästa</Button>
                        }
                        {step === "order" && showCreateBtn() &&
                            <Button variant="contained" color="secondary"
                                disabled={isCreatingOrder}
                                onClick={() => createAddon()}>Lägg till tillägg</Button>
                        }
                        <Button variant="contained" color="secondary"
                            disabled={isCreatingOrder}
                            onClick={() => onAbort()}>Avbryt</Button>
                    </Box>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default withAlert(AddEntryAddonDialog);
